// 83 §3 (AUTH-12) — THE DOOR FOR SOMEBODY WHO HAS NO PASSWORD.
//
// §3: "A new account in a passwordless tenant has no password to be given, so
// account creation requires a single-use, time-limited enrolment invitation."
// This is where that person lands: a link carrying the token, the texts, and
// enrolment at the end of it.
//
// ── OPENING THE LINK MUST NOT SPEND THE INVITATION ──────────────────────────
//
// Nothing is POSTed on mount. A mail scanner, a link-preview bot or a corporate
// gateway follows URLs, and an invitation spent by a machine that never read it
// would be burnt before the person ever saw this screen — with §3's single-use
// rule making that permanent. So acceptance is a POST behind a button, pressed
// by a person who has just read what they are agreeing to.
//
// ── THE CHOICE COMES BEFORE THE SPEND, AND CARRIES ITS OWN TEXT ─────────────
//
// Same principle the settings panel earned on the glance: a person chooses
// between things he can read. The shapes his firm permits are listed with
// AUTH-11's own `how` text beneath each, he picks one, and only then is the
// invitation spent — so somebody who opens the link, reads, and decides to do it
// later still has a live invitation.
//
// ── AND IT BUILDS NO SECOND ENROLMENT ──────────────────────────────────────
//
// Accepting yields the `enrol` ticket AUTH-7 already uses, so the ceremony is
// `AuthStepModal` in its ordinary enrol mode. AUTH-1's one look and feel is kept
// by there being nothing new to look at.
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { CT, RAD, FS } from "../lib/tokens";
import AuthStepModal from "../components/AuthStepModal";
import { FaceGlyph, FingerprintGlyph } from "../components/AuthGlyphs";
import { acceptInvitation } from "../api";
import type { InvitationAcceptResponse } from "../api";
import { AUTH_SHAPES, shapeForMode, installationTier, sellerFallbackKey } from "../lib/authShapes";
import type { AuthConfigLike } from "../lib/authShapes";

type ApiFailure = { message?: string; code?: string };

export default function InvitationView({
  token,
  onLogin,
  firmName = "Legal Practice Manager",
}: {
  token: string;
  onLogin?: (user: unknown, tok: string) => void;
  firmName?: string;
}) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState<AuthConfigLike | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<InvitationAcceptResponse | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    fetch("/api/auth/config", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (live) setCfg(c); })
      .catch(() => { if (live) setCfg(null); });
    return () => { live = false; };
  }, []);

  const pwMode = cfg?.passwordlessMode === "permissive" ? "permissive" : "strict";
  const tier = installationTier(cfg);
  const permitted = (cfg?.authMethodsEnabled || []).filter((x: string) =>
    AUTH_SHAPES.some((sh) => sh.id === x));

  const accept = async (shape: string) => {
    setErr("");
    setBusy(true);
    try {
      const res = await acceptInvitation(token);
      setChosen(shape);
      setAccepted(res);
    } catch (e) {
      const code = (e as ApiFailure)?.code || "";
      // Every refusal is one sentence server-side (a caller must not learn
      // whether a token was ever real), so the screen says the same thing and
      // points at the person who can issue another.
      setErr(
        code === "auth.account_disabled" || code === "auth.access_cut"
          ? t("invite.blocked", {
            fallback: "This account is not available. Contact your firm's administrator.",
          })
          : t("invite.invalid", {
            fallback:
              "This invitation is not valid — it may have been used already, or it may have expired. Ask your firm's administrator for a new one.",
          }),
      );
    } finally {
      setBusy(false);
    }
  };

  // Enrolment happens in THE modal, with the ticket the acceptance minted.
  if (accepted && chosen) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <h1 style={S.title}>{firmName}</h1>
          <AuthStepModal
            mode="enrol"
            challenge={accepted.challenge}
            shapes={[chosen]}
            cfg={cfg}
            onDone={(user?: unknown, tok?: string) => { if (tok) onLogin?.(user, tok); }}
            onCancel={() => { setAccepted(null); setChosen(null); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <h1 style={S.title}>{firmName}</h1>
        <p style={S.lede}>
          {t("invite.lede", {
            fallback:
              "You have been invited to set up how you sign in. Choose one below — you can add another later — and your firm will not give you a password.",
          })}
        </p>

        {err && <div style={S.err}>{err}</div>}

        {!cfg ? (
          <p style={S.hint}>{t("common.loading", { fallback: "Loading…" })}</p>
        ) : permitted.length === 0 ? (
          <p style={S.hint}>
            {t("invite.none_permitted", {
              fallback: "Your firm has not enabled any way to sign in yet. Contact your administrator.",
            })}
          </p>
        ) : (
          permitted.map((id: string) => {
            const sh = shapeForMode(AUTH_SHAPES.find((x) => x.id === id)!, pwMode);
            const isPasskey = id !== "totp";
            return (
              <div key={id} style={S.row}>
                <div style={S.rowMain}>
                  <div style={S.rowTitle}>
                    <span>{t(`auth.panel.${id}.title`, { fallback: t(sh.nameKey, { fallback: id }) })}</span>
                    {isPasskey && (
                      <span style={S.glyphs}><FaceGlyph /><FingerprintGlyph /></span>
                    )}
                  </div>
                  {/* 83 (glance) — THE SHORT COPY, and the four texts BEHIND a
                      disclosure. Rendering all four inline gave every option
                      three paragraphs and made the screen four times taller than
                      a choice needs; somebody deciding wants one line per option
                      and somebody who wants the risks asks for them.

                      This is the SAME two-audiences decision the settings panel
                      earned — and it is the same short text, from
                      `auth.panel.<id>.how`, so the two screens cannot drift into
                      two descriptions of one shape. AUTH-11's four are not lost
                      and are not weakened: they are one click away, in the same
                      `<details>` shape this tree already uses, and the lint that
                      proves all six keys resolve is untouched. */}
                  <p style={S.how}>{t(`auth.panel.${id}.how`, { fallback: "" })}</p>
                  <details style={S.details}>
                    <summary style={S.summary}>
                      {t("auth.about.link", { fallback: "How does this work?" })}
                    </summary>
                    <p style={S.risks}>
                      <strong>{t("auth.about.how", { fallback: "How it works" })}</strong>{" "}
                      {t(sh.howKey, { fallback: "" })}
                    </p>
                    <p style={S.risks}>
                      <strong>{t("auth.about.risks", { fallback: "Risks" })}</strong>{" "}
                      {t(sh.risksKey, { fallback: "" })}
                    </p>
                    <p style={S.fallback}>
                      {t(sh.fallbackFirmKey, { fallback: "" })}{" "}
                      {t(sellerFallbackKey(sh, tier), { fallback: "" })}
                    </p>
                  </details>
                </div>
                <div style={S.rowAction}>
                  <button type="button" className="btn btn-primary" disabled={busy}
                    onClick={() => accept(id)}>
                    {t("invite.choose", { fallback: "Set this up" })}
                  </button>
                </div>
              </div>
            );
          })
        )}

        <p style={S.foot}>
          {t("invite.single_use", {
            fallback:
              "This invitation works once. Nothing is used up until you press a button above.",
          })}
        </p>
      </div>
    </div>
  );
}

const S = {
  // 83 (glance) — IT MUST SCROLL ITSELF, because nothing above it will.
  // `src/App.css:81-88` sets `body, #root { height: calc(100dvh / …);
  // overflow-y: hidden }` — the app shell deliberately owns the viewport and
  // clips it — so a `min-height: 100vh` box simply grew into a region no
  // scrollbar reaches, and the third and fourth options could not be read at
  // all. `height: 100%` plus `overflow-y: auto` makes THIS box the scroller.
  //
  // And `margin: auto` on the card rather than `align-items: center` on the
  // page: centring a flex child that overflows makes its TOP unreachable, which
  // is the same defect wearing a different rule. `auto` margins centre while it
  // fits and yield to the scroller when it does not.
  page: {
    height: "100%",
    overflowY: "auto",
    display: "flex",
    justifyContent: "center",
    background: `var(--bg, ${CT.c_0f1115})`,
    padding: 20,
  } as const,
  card: {
    width: "100%",
    maxWidth: 620,
    // See `page` — this is what centres it, and what lets it scroll when tall.
    margin: "auto",
    background: `var(--bs, ${CT.c_161b26})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`,
    borderRadius: RAD.r10,
    padding: 22,
  } as const,
  title: { fontSize: FS.s16, color: `var(--tp, ${CT.c_fff})`, margin: "0 0 6px" } as const,
  lede: { fontSize: FS.s12, color: `var(--ts, ${CT.c_8892a4})`, margin: "0 0 14px", lineHeight: 1.6 } as const,
  row: { display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderTop: `1px solid var(--br, ${CT.c_2a2e37})` } as const,
  rowMain: { flex: 1, minWidth: 0 } as const,
  rowTitle: { display: "flex", alignItems: "center", gap: 6, fontSize: FS.s12_5, fontWeight: 600, color: `var(--tp, ${CT.c_fff})` } as const,
  glyphs: { display: "inline-flex", gap: 3, color: `var(--ac, ${CT.c_4a7cff})` } as const,
  how: { fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: "5px 0 0", lineHeight: 1.55 } as const,
  risks: { fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: "6px 0 0", lineHeight: 1.55 } as const,
  fallback: { fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: "6px 0 0", lineHeight: 1.55 } as const,
  rowAction: { flexShrink: 0 } as const,
  details: { marginTop: 6 } as const,
  summary: {
    fontSize: FS.s11,
    color: `var(--ac, ${CT.c_4a7cff})`,
    cursor: "pointer",
    listStyle: "revert",
  } as const,
  hint: { fontSize: FS.s11_5, color: `var(--tm, ${CT.c_9ca3af})` } as const,
  foot: { fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: "14px 0 0", lineHeight: 1.5 } as const,
  err: {
    fontSize: FS.s11_5,
    color: `var(--dg, ${CT.c_f87171})`,
    background: "rgba(248,113,113,.08)",
    border: "1px solid rgba(248,113,113,.3)",
    borderRadius: RAD.r6,
    padding: "8px 10px",
    margin: "0 0 12px",
  } as const,
};
